import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Timer, Play, Square, Clock, FolderKanban, Loader2 } from 'lucide-react';

const formatDuration = (seconds) => {
  if (!seconds) return '00:00:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const liveDuration = (startIso) => {
  if (!startIso) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(startIso).getTime()) / 1000));
};

const WorkerPersonalTime = () => {
  const [active, setActive] = useState([]);
  const [history, setHistory] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pulse, setPulse] = useState(0);
  const [showStartForm, setShowStartForm] = useState(false);
  const [selectedProject, setSelectedProject] = useState('');
  const [note, setNote] = useState('');
  const [acting, setActing] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [activeRes, histRes, projRes] = await Promise.all([
        api.getActivePersonalTime(),
        api.getPersonalTimeHistory(7),
        api.getProjects(),
      ]);
      setActive(activeRes.data || []);
      setHistory(histRes.data);
      setProjects(projRes.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (active.length === 0) return;
    const t = setInterval(() => setPulse((p) => p + 1), 1000);
    return () => clearInterval(t);
  }, [active.length]);

  const handleStart = async () => {
    if (!selectedProject) { toast.error('Välj ett projekt'); return; }
    setActing(true);
    try {
      await api.startPersonalTime({ project_id: selectedProject, note: note || null });
      toast.success('Personlig timer startad');
      setSelectedProject('');
      setNote('');
      setShowStartForm(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte starta');
    } finally {
      setActing(false);
    }
  };

  const handleStop = async (entryId) => {
    try {
      await api.stopPersonalTime(entryId, {});
      toast.success('Timer stoppad');
      fetchAll();
    } catch {
      toast.error('Kunde inte stoppa');
    }
  };

  const activeProjectIds = active.map(e => e.project_id);
  const availableProjects = projects.filter(p => !activeProjectIds.includes(p.id) && p.status === 'active');

  if (loading) return <div className="text-center py-10"><Loader2 className="animate-spin mx-auto text-[#E8613A]" /></div>;

  return (
    <div className="space-y-3 pb-20" data-testid="worker-personal-time">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold flex items-center gap-2">
          <Timer size={20} className="text-[#E8613A]" /> Personlig tid
        </h1>
      </div>
      <p className="text-xs text-muted-foreground -mt-1">
        Spåra din tid på projekt utan att rapportera. Datan sparas och admin kan se sammanställning.
      </p>

      {/* Active timers */}
      {active.length > 0 && (
        <div className="space-y-2">
          {active.map(e => (
            <div key={e.id} className="bg-gradient-to-br from-[#E8613A]/10 to-[#E8613A]/5 border-2 border-[#E8613A]/30 rounded-xl p-4" data-testid={`active-timer-${e.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-widest text-[#E8613A] font-bold">Aktiv timer</p>
                  <p className="text-sm font-bold truncate mt-0.5">{e.project_name}</p>
                  {e.note && <p className="text-xs text-muted-foreground mt-1 truncate">{e.note}</p>}
                </div>
                <Button size="sm" onClick={() => handleStop(e.id)} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 h-9" data-testid={`stop-timer-${e.id}`}>
                  <Square size={12} className="mr-1" /> Stoppa
                </Button>
              </div>
              <p className="text-3xl font-bold font-mono mt-2 text-[#E8613A]" data-testid={`live-${e.id}`}>{formatDuration(liveDuration(e.started_at) + pulse * 0)}</p>
            </div>
          ))}
        </div>
      )}

      {/* Start new timer */}
      {!showStartForm ? (
        <Button onClick={() => setShowStartForm(true)} className="w-full bg-[#E8613A] hover:bg-[#D4532F] text-white rounded-xl h-12 font-semibold" data-testid="start-new-timer">
          <Play size={14} className="mr-2" /> Starta ny timer
        </Button>
      ) : (
        <div className="bg-card border border-border rounded-xl p-4 space-y-3" data-testid="start-form">
          <div>
            <Label className="text-xs">Projekt</Label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="mt-1.5 w-full h-10 px-3 rounded-md border border-border bg-background text-sm"
              data-testid="select-project"
            >
              <option value="">Välj projekt...</option>
              {availableProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            {availableProjects.length === 0 && <p className="text-xs text-muted-foreground mt-1">Du har redan aktiva timers på alla projekt.</p>}
          </div>
          <div>
            <Label className="text-xs">Notering (valfritt)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="T.ex. installerade kök" className="mt-1.5" data-testid="timer-note" />
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowStartForm(false)} variant="outline" className="flex-1">Avbryt</Button>
            <Button onClick={handleStart} disabled={acting || !selectedProject} className="flex-1 bg-[#E8613A] hover:bg-[#D4532F] text-white" data-testid="confirm-start">
              {acting ? <Loader2 size={14} className="animate-spin" /> : 'Starta'}
            </Button>
          </div>
        </div>
      )}

      {/* History - 7 days */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h2 className="text-sm font-bold flex items-center gap-2"><Clock size={14} className="text-[#E8613A]" /> Senaste 7 dagar</h2>
          <span className="text-xs text-muted-foreground">{formatDuration(history?.total_seconds || 0)} totalt</span>
        </div>
        <div className="divide-y divide-border">
          {(history?.by_project || []).length === 0 ? (
            <p className="text-sm text-muted-foreground p-4 text-center">Ingen tid loggad ännu</p>
          ) : (history?.by_project || []).map(p => (
            <div key={p.project_id} className="px-4 py-3 flex items-center justify-between" data-testid={`history-${p.project_id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <FolderKanban size={14} className="text-muted-foreground flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{p.project_name}</p>
                  <p className="text-[10px] text-muted-foreground">{p.sessions} sessioner</p>
                </div>
              </div>
              <span className="text-sm font-mono font-bold">{formatDuration(p.total_seconds)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WorkerPersonalTime;
